import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/********************************************************************************
 * Copyright (c) 2019 TypeFox and others
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License v. 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 ********************************************************************************/
import { Fragment, useContext, useState } from 'react';
import { Box, Typography, Divider, Link, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { MainContext } from '../../context';
import { toLocalTime } from '../../utils';
import { isEqualUser } from '../../extension-registry-types';
import { TextDivider } from '../../components/text-divider';
import { DelayedLoadIndicator } from '../../components/delayed-load-indicator';
import { ButtonWithProgress } from '../../components/button-with-progress';
import { Timestamp } from '../../components/timestamp';
import { ExtensionRatingStars } from './extension-rating-stars';
import { ExtensionReviewDialog } from './extension-review-dialog';
import { LoginComponent } from '../../default/login';
import { hasPermission } from '../../permissions';
import { useDeleteReview, useDeleteUserReview, useExtensionReviews } from './use-extension-reviews';
export const ExtensionDetailReviews = props => {
    const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
    const [removeCommentFromUser, setRemoveCommentFromUser] = useState(undefined);
    const context = useContext(MainContext);
    const { data: reviewList, isFetching: reviewsLoading } = useExtensionReviews(props.extension);
    const { mutate: deleteReview, isPending: revoking } = useDeleteReview(props.extension);
    const { mutate: deleteUserReview, isPending: removingReview, variables: removingReviewOf } = useDeleteUserReview(props.extension);
    const handleRevokeButton = () => deleteReview(reviewList.deleteUrl);
    const renderButton = () => {
        // An anonymous visitor gets the login in place of the review button. It doesn't depend on
        // reviewList, so it comes before the check the signed-in branches below need.
        if (!context.user) {
            // userLoading: undefined user is not yet logged out, and prompting one who is signed in
            // would send them through OAuth again
            if (context.userLoading || !context.loginProviders) {
                return '';
            }
            return (_jsx(Box, { children: _jsx(LoginComponent, { loginProviders: context.loginProviders, renderButton: (href, onClick) => (_jsx(Button, { variant: 'contained', color: 'secondary', href: href, onClick: onClick, children: "Log in to Review" })) }) }));
        }
        if (!reviewList) {
            return '';
        }
        const existingReview = reviewList.reviews.find(r => isEqualUser(r.user, context.user));
        if (existingReview) {
            const localTime = toLocalTime(existingReview.timestamp);
            return (_jsx(ButtonWithProgress, { working: revoking, onClick: handleRevokeButton, title: `Revoke review written by ${context.user.loginName} on ${localTime}`, children: "Revoke my Review" }));
        }
        else {
            return (_jsx(Box, { children: _jsx(ExtensionReviewDialog, { extension: props.extension, reviewPostUrl: reviewList.postUrl }) }));
        }
    };
    const handleAdminRemoveReviewButton = () => {
        if (removeCommentFromUser === undefined) {
            return;
        }
        deleteUserReview(removeCommentFromUser, { onSuccess: () => setRemoveDialogOpen(false) });
    };
    const renderAdminRemoveButton = (r) => {
        return (_jsx(Button, { variant: 'contained', color: 'error', sx: { ml: 1 }, onClick: () => {
                setRemoveCommentFromUser(r.user);
                setRemoveDialogOpen(true);
            }, children: "Remove review" }));
    };
    const renderAdminRemoveDialog = () => {
        return (_jsxs(Dialog, { open: removeDialogOpen, onClose: () => setRemoveDialogOpen(false), children: [_jsx(DialogTitle, { children: "Remove Review" }), _jsx(DialogContent, { children: _jsx(DialogContentText, { component: 'div', children: _jsxs(Typography, { children: ["Confirm removal of review comment from ", _jsx("code", { children: removeCommentFromUser?.loginName }), "?"] }) }) }), _jsxs(DialogActions, { children: [_jsx(Button, { variant: 'contained', color: 'primary', onClick: () => setRemoveDialogOpen(false), children: "Cancel" }), _jsx(ButtonWithProgress, { autoFocus: true, color: 'error', sx: { ml: 1 }, working: removingReview && removingReviewOf?.loginName === removeCommentFromUser?.loginName, onClick: () => handleAdminRemoveReviewButton(), children: "Remove review" })] })] }));
    };
    const renderReviewList = (list) => {
        if (!list) {
            return '';
        }
        if (list.reviews.length === 0) {
            return (_jsx(Box, { mt: 3, children: _jsx(Typography, { children: "Be the first to review this extension" }) }));
        }
        return list.reviews.map(renderReview.bind(this));
    };
    const renderReview = (r) => {
        return (_jsxs(Fragment, { children: [_jsxs(Box, { display: 'flex', justifyContent: 'space-between', children: [_jsxs(Box, { my: 2, children: [_jsxs(Box, { display: 'flex', children: [r.timestamp ? (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: 'body2', children: _jsx(Timestamp, { value: r.timestamp }) }), _jsx(TextDivider, {})] })) : null, _jsx(Typography, { variant: 'body2', children: r.user.homepage ? (_jsx(Link, { href: r.user.homepage, color: 'text.primary', underline: 'hover', children: r.user.loginName })) : (r.user.loginName) })] }), _jsx(Box, { display: 'flex', alignItems: 'center', children: _jsx(ExtensionRatingStars, { number: r.rating }) }), _jsx(Box, { overflow: 'auto', children: _jsx(Typography, { variant: 'body1', sx: { overflow: 'hidden', textOverflow: 'ellipsis' }, children: r.comment }) })] }), hasPermission(context.user, 'manage_extensions') ? (_jsx(Box, { sx: { mb: 2, minWidth: 160 }, display: 'flex', alignItems: 'end', children: renderAdminRemoveButton(r) })) : null] }), _jsx(Divider, {})] }, r.user.loginName + r.timestamp));
    };
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { sx: {
                    mb: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    ['@media(max-width: 360px)']: {
                        flexDirection: 'column',
                        '& > div:first-of-type': {
                            marginBottom: '1rem'
                        },
                        '& button': {
                            maxWidth: '12rem'
                        }
                    }
                }, children: [_jsx(Box, { children: _jsx(Typography, { variant: 'h5', children: "User Reviews" }) }), renderButton()] }), _jsx(Divider, {}), _jsxs(Box, { children: [_jsx(DelayedLoadIndicator, { loading: reviewsLoading }), renderReviewList(reviewList), hasPermission(context.user, 'manage_extensions') && renderAdminRemoveDialog()] })] }));
};
//# sourceMappingURL=extension-detail-reviews.js.map